import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { ChevronDown, ChevronRight, Users, Mail, Phone, User, MapPin, Calendar, Droplet, Book, Heart, UserCheck, Award, Loader2, LayoutGrid, Table as TableIcon, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

const StudentListWidget = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [filterDivision, setFilterDivision] = useState('All');
  const [divisions, setDivisions] = useState([]);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [sortConfig, setSortConfig] = useState({ key: 'rollNumber', direction: 'asc' });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const studentsRef = collection(db, 'students');
      const q = query(studentsRef, orderBy('rollNumber', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const studentsList = [];
      const divisionSet = new Set();
      
      querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        studentsList.push(data);
        if (data.division) {
          divisionSet.add(data.division);
        }
      });
      
      setStudents(studentsList);
      setDivisions(['All', ...Array.from(divisionSet).sort()]);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to load students. Please try again.');
      setLoading(false);
    }
  };

  const toggleStudent = (rollNumber) => {
    setExpandedStudent(expandedStudent === rollNumber ? null : rollNumber);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedStudents = [...students].sort((a, b) => {
    const aValue = a[sortConfig.key] || '';
    const bValue = b[sortConfig.key] || '';
    
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredStudents = filterDivision === 'All' 
    ? sortedStudents 
    : sortedStudents.filter(s => s.division === filterDivision);

  const downloadPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Add title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('YBIT Student List', 14, 15);
    
    // Add metadata
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const today = new Date().toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Generated on: ${today}`, 14, 22);
    
    if (filterDivision !== 'All') {
      doc.text(`Division: ${filterDivision}`, 14, 27);
    }
    
    // Prepare table data - matching the new column order
    const tableData = filteredStudents.map(student => [
      student.rollNumber || '-',
      student.surname || '-',
      student.firstName || '-',
      student.fatherName || '-',
      student.dob || '-',
      student.gender || '-',
      student.currentAddress || '-',
      student.permanentAddress || '-',
      student.aadharCard || '-',
      student.bloodGroup || '-',
      student.abcId || '-',
      student.hobbies || '-',
      student.mobile || '-',
      student.whatsapp || '-',
      student.email || '-',
      student.fatherMobile || '-',
      student.fatherOccupation || '-',
      student.motherName || '-',
      student.motherMobile || '-',
      student.motherOccupation || '-'
    ]);

    // Add table with new column order
    doc.autoTable({
      head: [[
        'Roll Number',
        'Surname',
        'First Name',
        'Father Name',
        'DOB',
        'Gender',
        'Current Address',
        'Permanent Address',
        'Aadhar Card',
        'Blood Group',
        'ABC ID',
        'Hobbies',
        'Mobile',
        'WhatsApp',
        'Email',
        'Father Mobile',
        'Father Occupation',
        'Mother Name',
        'Mother Mobile',
        'Mother Occupation'
      ]],
      body: tableData,
      startY: filterDivision !== 'All' ? 32 : 27,
      styles: { 
        fontSize: 6,
        cellPadding: 1.5,
      },
      headStyles: {
        fillColor: [37, 99, 235],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 6
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      columnStyles: {
        0: { cellWidth: 13 },  // Roll Number
        1: { cellWidth: 15 },  // Surname
        2: { cellWidth: 15 },  // First Name
        3: { cellWidth: 18 },  // Father Name
        4: { cellWidth: 13 },  // DOB
        5: { cellWidth: 10 },  // Gender
        6: { cellWidth: 25 },  // Current Address
        7: { cellWidth: 25 },  // Permanent Address
        8: { cellWidth: 15 },  // Aadhar Card
        9: { cellWidth: 10 },  // Blood Group
        10: { cellWidth: 12 }, // ABC ID
        11: { cellWidth: 20 }, // Hobbies
        12: { cellWidth: 14 }, // Mobile
        13: { cellWidth: 14 }, // WhatsApp
        14: { cellWidth: 25 }, // Email
        15: { cellWidth: 14 }, // Father Mobile
        16: { cellWidth: 18 }, // Father Occupation
        17: { cellWidth: 18 }, // Mother Name
        18: { cellWidth: 14 }, // Mother Mobile
        19: { cellWidth: 18 }  // Mother Occupation
      },
      margin: { left: 10, right: 10 }
    });

    // Save the PDF
    const filename = filterDivision !== 'All' 
      ? `YBIT_Students_Division_${filterDivision}_${new Date().toISOString().split('T')[0]}.pdf`
      : `YBIT_Students_All_${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(filename);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading students...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchStudents}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-800">Student List</h2>
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            {filteredStudents.length} Students
          </span>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                viewMode === 'card' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="text-sm font-medium">Cards</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                viewMode === 'table' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <TableIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Table</span>
            </button>
          </div>

          {/* Division Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Division:</label>
            <select
              value={filterDivision}
              onChange={(e) => setFilterDivision(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {divisions.map(div => (
                <option key={div} value={div}>{div}</option>
              ))}
            </select>
          </div>

          {/* PDF Download Button - Only show in table view */}
          {viewMode === 'table' && (
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Download PDF</span>
            </button>
          )}
        </div>
      </div>

      {/* Conditional View Rendering */}
      {viewMode === 'card' ? (
        <CardView 
          students={filteredStudents}
          expandedStudent={expandedStudent}
          toggleStudent={toggleStudent}
        />
      ) : (
        <TableView 
          students={filteredStudents}
          sortConfig={sortConfig}
          handleSort={handleSort}
        />
      )}
    </div>
  );
};

// Card View Component
const CardView = ({ students, expandedStudent, toggleStudent }) => {
  if (students.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No students found for this division.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {students.map((student) => (
        <div 
          key={student.id} 
          className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
        >
          {/* Collapsed Header */}
          <div 
            onClick={() => toggleStudent(student.rollNumber)}
            className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white cursor-pointer hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-4">
              {expandedStudent === student.rollNumber ? (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-600" />
              )}
              
              {/* Photo Thumbnail */}
              {getStudentPhotoUrl(student) ? (
                <img 
                  src={getStudentPhotoUrl(student)} 
                  alt={`${student.firstName} ${student.surname}`}
                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
              )}
              
              <div>
                <div className="font-semibold text-gray-800 text-lg">
                  {student.rollNumber}
                </div>
                <div className="text-sm text-gray-600">
                  {student.firstName} {student.surname}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                Division {student.division}
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                Batch {student.batch}
              </span>
            </div>
          </div>

          {/* Expanded Details */}
          {expandedStudent === student.rollNumber && (
            <div className="p-6 bg-white border-t border-gray-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Personal Details Section */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />
                      Personal Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <DetailRow label="Full Name" value={`${student.firstName} ${student.surname}`} />
                      <DetailRow label="Father's Name" value={student.fatherName} />
                      <DetailRow label="Date of Birth" value={student.dob} icon={Calendar} />
                      <DetailRow label="Gender" value={student.gender} />
                      <DetailRow label="Blood Group" value={student.bloodGroup} icon={Droplet} />
                      <DetailRow label="Aadhar Card" value={student.aadharCard} />
                      <DetailRow label="ABC ID" value={student.abcId} />
                    </div>
                  </div>

                  {/* Contact Details Section */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-600" />
                      Contact Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <DetailRow label="Email" value={student.email} icon={Mail} />
                      <DetailRow label="Mobile" value={student.mobile} icon={Phone} />
                      <DetailRow label="WhatsApp" value={student.whatsapp} icon={Phone} />
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-600" />
                      Address Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <DetailRow label="Current Address" value={student.currentAddress} />
                      <DetailRow label="Permanent Address" value={student.permanentAddress} />
                    </div>
                  </div>

                  {/* Hobbies Section */}
                  {student.hobbies && (
                    <div className="bg-pink-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-600" />
                        Hobbies/Interests
                      </h3>
                      <p className="text-sm text-gray-700">{student.hobbies}</p>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Photo Section */}
                  {getStudentPhotoUrl(student) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-800 mb-3">Student Photo</h3>
                      <img 
                        src={getStudentPhotoUrl(student)} 
                        alt={`${student.firstName} ${student.surname}`}
                        className="w-full max-w-xs mx-auto rounded-lg shadow-md border-2 border-gray-200"
                      />
                    </div>
                  )}

                  {/* Academic Details Section */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Book className="w-4 h-4 text-purple-600" />
                      Academic Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <DetailRow label="Branch" value={student.branch} />
                      <DetailRow label="Division" value={student.division} />
                      <DetailRow label="Batch" value={student.batch} />
                      <DetailRow label="Roll Number" value={student.rollNumber} />
                    </div>
                  </div>

                  {/* Parent Details Section */}
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-yellow-600" />
                      Parent Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="font-medium text-gray-700">Father:</p>
                        <DetailRow label="Name" value={student.fatherName} />
                        <DetailRow label="Occupation" value={student.fatherOccupation} />
                        <DetailRow label="Mobile" value={student.fatherMobile} icon={Phone} />
                      </div>
                      <div className="border-t border-yellow-200 pt-3">
                        <p className="font-medium text-gray-700">Mother:</p>
                        <DetailRow label="Name" value={student.motherName} />
                        <DetailRow label="Occupation" value={student.motherOccupation} />
                        <DetailRow label="Mobile" value={student.motherMobile} icon={Phone} />
                      </div>
                    </div>
                  </div>

                  {/* Mentor Details Section */}
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-indigo-600" />
                      Mentor Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <DetailRow label="Mentor Name" value={student.mentorName} />
                      <DetailRow label="Mentor Email" value={student.mentorEmail} icon={Mail} />
                    </div>
                  </div>

                  {/* Coordinator Details Section */}
                  <div className="bg-teal-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Award className="w-4 h-4 text-teal-600" />
                      Class Coordinator
                    </h3>
                    <div className="space-y-2 text-sm">
                      <DetailRow label="Coordinator Name" value={student.coordinatorName || student.ccName} />
                      <DetailRow label="Coordinator Email" value={student.coordinatorEmail} icon={Mail} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Table View Component
const TableView = ({ students, sortConfig, handleSort }) => {
  if (students.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No students found for this division.
      </div>
    );
  }

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-300 ml-1 text-xs">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? (
      <span className="text-white ml-1 text-xs">↑</span>
    ) : (
      <span className="text-white ml-1 text-xs">↓</span>
    );
  };

  return (
    <div className="overflow-x-auto border border-gray-300 rounded-xl shadow-sm">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gradient-to-r from-blue-600 to-blue-500 text-white sticky top-0 shadow-md">
          <tr>
            <th 
              onClick={() => handleSort('rollNumber')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400"
            >
              Roll Number <SortIcon columnKey="rollNumber" />
            </th>
            <th 
              onClick={() => handleSort('surname')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400 whitespace-nowrap"
            >
              Surname <SortIcon columnKey="surname" />
            </th>
            <th 
              onClick={() => handleSort('firstName')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400 whitespace-nowrap"
            >
              First Name <SortIcon columnKey="firstName" />
            </th>
            <th 
              onClick={() => handleSort('fatherName')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400 whitespace-nowrap"
            >
              Father Name <SortIcon columnKey="fatherName" />
            </th>
            <th 
              onClick={() => handleSort('dob')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400"
            >
              DOB <SortIcon columnKey="dob" />
            </th>
            <th 
              onClick={() => handleSort('gender')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400"
            >
              Gender <SortIcon columnKey="gender" />
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Current Address
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Permanent Address
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Aadhar Card
            </th>
            <th 
              onClick={() => handleSort('bloodGroup')}
              className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-all border-r border-blue-400 whitespace-nowrap"
            >
              Blood Group <SortIcon columnKey="bloodGroup" />
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              ABC ID
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400">
              Hobbies
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400">
              Mobile
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400">
              WhatsApp
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400">
              Email
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Father Mobile
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Father Occupation
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Mother Name
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide border-r border-blue-400 whitespace-nowrap">
              Mother Mobile
            </th>
            <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap">
              Mother Occupation
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {students.map((student, index) => (
            <tr 
              key={student.id}
              className={`hover:bg-blue-50 transition-all duration-150 ${
                index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              }`}
            >
              <td className="px-5 py-3.5 text-sm font-semibold text-gray-900 whitespace-nowrap border-r border-gray-200">
                {student.rollNumber || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.surname || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.firstName || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.fatherName || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.dob || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 border-r border-gray-200">
                {student.gender || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-700 border-r border-gray-200 max-w-xs">
                <div className="truncate" title={student.currentAddress}>
                  {student.currentAddress || '-'}
                </div>
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-700 border-r border-gray-200 max-w-xs">
                <div className="truncate" title={student.permanentAddress}>
                  {student.permanentAddress || '-'}
                </div>
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.aadharCard || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 text-center border-r border-gray-200">
                {student.bloodGroup || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.abcId || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-700 border-r border-gray-200 max-w-xs">
                <div className="truncate" title={student.hobbies}>
                  {student.hobbies || '-'}
                </div>
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.mobile || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.whatsapp || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 border-r border-gray-200">
                {student.email || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.fatherMobile || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.fatherOccupation || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.motherName || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap border-r border-gray-200">
                {student.motherMobile || '-'}
              </td>
              <td className="px-5 py-3.5 text-sm text-gray-800 whitespace-nowrap">
                {student.motherOccupation || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Helper Component for Detail Rows
const DetailRow = ({ label, value, icon: Icon }) => {
  if (!value) return null;
  
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />}
      <div className="flex-1">
        <span className="font-medium text-gray-600">{label}:</span>
        <span className="ml-2 text-gray-800">{value}</span>
      </div>
    </div>
  );
};

export default StudentListWidget;
